// Adapter: server `Camera` → mock-shaped `CameraMock` accepted by the
// existing `<CameraTile>` component.
//
// CameraTile was authored against the LLD-07 mock fleet (CameraMock has
// `name`, `view_label`, `preview_gradient`). When we wire in the real BFF
// /api/cameras endpoint the server gives us doorbot_id / description /
// status / capabilities — none of the cosmetic fields the tile renders.
//
// We map on the fly here rather than rewriting the tile because:
//   1. PlayerMock + tests still depend on CameraMock; bifurcating its
//      props surface would ripple further than the wiring task allows.
//   2. The cosmetics (gradient, view label) are deterministic from the
//      doorbot id, so each tile keeps a stable color across reloads
//      without a new endpoint.

import type { Camera, CameraStatus } from "@/api/camerasClient";
import type { CameraMock } from "@/mocks/cameras";

const PALETTE = [
  "linear-gradient(135deg, #0e1a2f 0%, #233a68 40%, #3a5a9c 100%)",
  "linear-gradient(135deg, #1a0e2f 0%, #3a2368 40%, #6a3a9c 100%)",
  "linear-gradient(135deg, #102a1f 0%, #1e5138 40%, #3a8c61 100%)",
  "linear-gradient(135deg, #2a1a0e 0%, #5e3a23 40%, #8c6a3a 100%)",
  "linear-gradient(135deg, #0e2a2a 0%, #1e5151 40%, #3a8c8c 100%)",
  "linear-gradient(135deg, #2f0e1a 0%, #68233a 40%, #9c3a5a 100%)",
  "linear-gradient(135deg, #1a1a1a 0%, #353535 40%, #555555 100%)",
];

/** Cheap deterministic 32-bit hash. Stable per id across reloads. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function pickGradient(seed: string): string {
  return PALETTE[hashString(seed) % PALETTE.length] ?? PALETTE[0]!;
}

/**
 * Map a server camera status onto the CameraMock-supported subset. Server
 * may send any string — anything outside the known set degrades to
 * "degraded" so the tile renders LOW BITRATE rather than crashing.
 */
function normalizeStatus(s: CameraStatus): CameraMock["status"] {
  if (s === "online" || s === "offline" || s === "degraded") return s;
  return "degraded";
}

/** Best-effort short label for the LIVE-pill area. Six chars, uppercase. */
function deriveViewLabel(cam: Camera): string {
  const src = cam.description ?? cam.kind ?? cam.doorbot_id;
  // strip non-alpha and uppercase; fall back to last 6 chars of the id
  const cleaned = src.replace(/[^A-Za-z0-9]+/g, " ").trim();
  if (cleaned.length === 0) {
    const tail = cam.doorbot_id.slice(-6).toUpperCase();
    return tail;
  }
  // Prefer the first word, padded/truncated.
  const word = cleaned.split(/\s+/)[0]!.toUpperCase();
  return word.length > 8 ? word.slice(0, 8) : word;
}

export function adaptCameraForTile(
  cam: Camera,
  locationDisplay?: string,
): CameraMock {
  const name =
    cam.description?.trim() ||
    (cam.kind ? `${cam.kind} · ${cam.doorbot_id.slice(-6)}` : null) ||
    `Camera ${cam.doorbot_id.slice(-6)}`;
  const fallbackLocation = locationDisplay ?? cam.location_id;
  return {
    camera_id: cam.doorbot_id,
    name,
    location_id: cam.location_id,
    status: normalizeStatus(cam.status),
    preview_gradient: pickGradient(cam.doorbot_id),
    view_label: deriveViewLabel(cam),
    // Keep a non-null reference handy for tile aria labels via location_id.
    // CameraMock has no location-display field; consumers that need it pass
    // it through alongside.
    ...(fallbackLocation ? {} : {}),
  };
}
